import { useParams } from 'react-router-dom';
import { useSubmission } from '../../api/submissions';
import { Card } from '../../components/Card';
import { CopyButton } from '../../components/CopyButton';
import { JsonViewer } from '../../components/JsonViewer';
import { PageHeader } from '../../components/PageHeader';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './SubmissionDetailPage.module.css';

export function SubmissionDetailPage() {
	const { id, subId } = useParams<{ id: string; subId: string }>();

	const { data: submission, isLoading, error } = useSubmission(id ?? '', subId ?? '');

	const shortId = subId ? `${subId.slice(0, 8)}…` : 'Submission';
	const listBreadcrumbs = useProjectBreadcrumbs('Submissions');
	const detailBreadcrumbs = useProjectBreadcrumbs('Submissions', shortId);

	if (isLoading) {
		return (
			<div>
				<PageHeader title="Loading…" breadcrumbs={listBreadcrumbs} />
			</div>
		);
	}

	if (error || !submission) {
		return (
			<div>
				<PageHeader title="Submission" breadcrumbs={listBreadcrumbs} />
				<Card>
					<p className={shared.errorMessage}>
						{error instanceof Error ? error.message : 'Submission not found'}
					</p>
				</Card>
			</div>
		);
	}

	const hasUtm =
		submission.utm_source ||
		submission.utm_medium ||
		submission.utm_campaign ||
		submission.utm_content ||
		submission.utm_term;

	return (
		<div>
			<PageHeader title={`Submission ${shortId}`} breadcrumbs={detailBreadcrumbs} />

			<div className={shared.content}>
				{/* Metadata Card */}
				<Card>
					<h2 className={shared.cardHeading}>Metadata</h2>
					<dl className={shared.detailList}>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Form ID</dt>
							<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
								<span className={shared.mono}>{submission.form_id}</span>
							</dd>
						</div>

						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Identity</dt>
							<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
								<span className={styles.identityBadge}>{submission.identity.type}</span>{' '}
								<span className={shared.mono}>{submission.identity.value}</span>
							</dd>
						</div>

						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Submitted</dt>
							<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
								<span className={shared.mono}>{submission.submitted_at}</span>
								<span className={styles.secondaryText}>
									{' '}
									— {formatRelativeTime(submission.submitted_at)}
								</span>
							</dd>
						</div>

						{submission.page_url && (
							<div className={shared.detailRow}>
								<dt className={shared.detailLabel}>Page URL</dt>
								<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
									<span className={shared.mono}>{submission.page_url}</span>
								</dd>
							</div>
						)}

						{submission.referrer && (
							<div className={shared.detailRow}>
								<dt className={shared.detailLabel}>Referrer</dt>
								<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
									<span className={shared.mono}>{submission.referrer}</span>
								</dd>
							</div>
						)}

						{hasUtm && (
							<>
								{submission.utm_source && (
									<div className={shared.detailRow}>
										<dt className={shared.detailLabel}>UTM Source</dt>
										<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
											<span className={shared.mono}>{submission.utm_source}</span>
										</dd>
									</div>
								)}
								{submission.utm_medium && (
									<div className={shared.detailRow}>
										<dt className={shared.detailLabel}>UTM Medium</dt>
										<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
											<span className={shared.mono}>{submission.utm_medium}</span>
										</dd>
									</div>
								)}
								{submission.utm_campaign && (
									<div className={shared.detailRow}>
										<dt className={shared.detailLabel}>UTM Campaign</dt>
										<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
											<span className={shared.mono}>{submission.utm_campaign}</span>
										</dd>
									</div>
								)}
								{submission.utm_content && (
									<div className={shared.detailRow}>
										<dt className={shared.detailLabel}>UTM Content</dt>
										<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
											<span className={shared.mono}>{submission.utm_content}</span>
										</dd>
									</div>
								)}
								{submission.utm_term && (
									<div className={shared.detailRow}>
										<dt className={shared.detailLabel}>UTM Term</dt>
										<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
											<span className={shared.mono}>{submission.utm_term}</span>
										</dd>
									</div>
								)}
							</>
						)}

						{submission.experiment_variant && (
							<div className={shared.detailRow}>
								<dt className={shared.detailLabel}>Experiment</dt>
								<dd className={[shared.detailValue, styles.detailValue].join(' ')}>
									<span className={shared.mono}>{submission.experiment_variant.experiment_id}</span>
									<span className={styles.secondaryText}> / </span>
									<span className={shared.mono}>{submission.experiment_variant.variant}</span>
								</dd>
							</div>
						)}
					</dl>
				</Card>

				{/* Payload Card */}
				<Card>
					<div className={styles.payloadHeader}>
						<h2 className={[shared.cardHeading, styles.payloadHeading].join(' ')}>Form Data</h2>
						<CopyButton text={JSON.stringify(submission.data, null, 2)} displayText="Copy JSON" />
					</div>
					<JsonViewer data={submission.data} />
				</Card>
			</div>
		</div>
	);
}
